import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, SESSION_EXPIRED, sessionEvents } from '../../api/client';
import { actionKey, workspaceAction, workspaceChanges, workspaceRecords, workspaceSchema, type OperationReceipt, type SourceId, type ViewConfig, type ViewDefinition, type WorkspaceQuery } from '../../api/hub/workspace';

export const workspaceKeys = { schema: ['hub', 'workspace', 'schema'] as const, query: ['hub', 'workspace', 'query'] as const };
/** An independent browser cursor never reads or advances per-Key agent_changes_read. */
export function useWorkspaceRefresh() {
  const qc = useQueryClient();
  useEffect(() => {
    let stopped = false, cursor: string | undefined, failures = 0, epoch = 0, timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    const stop = () => { stopped = true; epoch++; clearTimeout(timer); controller?.abort(); };
    const schedule = () => {
      if (!stopped) timer = setTimeout(poll, document.hidden ? 30_000 : Math.min(30_000, 2_000 * 2 ** failures));
    };
    const poll = async () => {
      clearTimeout(timer);
      if (stopped) return;
      const attempt = ++epoch;
      controller?.abort(); controller = new AbortController();
      try {
        let changed = false, more = true;
        const seen = new Set<string>();
        while (more && !stopped) {
          const result = await workspaceChanges(cursor, controller.signal);
          if (stopped || attempt !== epoch) return;
          changed ||= result.items.length > 0;
          if (seen.has(result.nextCursor)) break;
          seen.add(result.nextCursor);
          cursor = result.nextCursor; more = result.hasMore;
        }
        failures = 0;
        if (changed && !stopped) await qc.invalidateQueries({ queryKey: ['hub'] });
      } catch (error) {
        if (stopped || attempt !== epoch) return;
        if (error instanceof ApiError && [401, 403].includes(error.status)) return stop();
        if ((error as Error).name !== 'AbortError') failures++;
      }
      if (attempt === epoch) schedule();
    };
    const visible = () => { if (!document.hidden && !stopped) void poll(); };
    document.addEventListener('visibilitychange', visible);
    sessionEvents.addEventListener(SESSION_EXPIRED, stop);
    void poll();
    return () => { stop(); document.removeEventListener('visibilitychange', visible); sessionEvents.removeEventListener(SESSION_EXPIRED, stop); };
  }, [qc]);
}
export function useWorkspaceRecords<T>(query: WorkspaceQuery, enabled = true) {
  return useQuery({ queryKey: [...workspaceKeys.query, query], queryFn: ({ signal }) => workspaceRecords<T>(query, signal), enabled, retry: (count, e) => !(e instanceof ApiError && [400, 401, 403, 404].includes(e.status)) && count < 2 });
}
const configOf = ({ name, source, layout, filters, sorts, groupBy, display }: ViewDefinition): ViewConfig => ({ name, source, layout, filters, sorts, groupBy, display });
export function useWorkspaceView(source: SourceId) {
  useWorkspaceRefresh();
  const qc = useQueryClient();
  const schema = useQuery({ queryKey: workspaceKeys.schema, queryFn: ({ signal }) => workspaceSchema(signal) });
  const views = (schema.data?.views ?? []).filter(v => v.source === source);
  const [activeViewId, setActive] = useState(() => sessionStorage.getItem(`workspace:${source}:view`) ?? '');
  const [baseline, setBaseline] = useState<ViewDefinition | null>(null);
  const [draft, setDraft] = useState<ViewConfig>({ name: '', source, layout: source === 'tasks' ? 'board' : 'calendar', filters: {}, sorts: [], groupBy: source === 'tasks' ? 'assigneeId' : null, display: {} });
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [receipt, setReceipt] = useState<OperationReceipt | null>(null);
  const seenInSchema = useRef(new Set<string>());
  const retained = useRef(new Map<string, { baseline: ViewDefinition | null; draft: ViewConfig; conflict: boolean }>());
  const dirty = !!baseline && JSON.stringify(draft) !== JSON.stringify(configOf(baseline));
  const remote = views.find(v => v.id === activeViewId);
  if (remote) seenInSchema.current.add(remote.id);
  const remoteChanged = !!baseline && (remote ? remote.version > baseline.version : seenInSchema.current.has(activeViewId));
  useEffect(() => {
    if (!schema.data) return;
    const initial = views.find(v => v.scope === 'system') ?? views[0];
    if (!activeViewId && initial) { setActive(initial.id); setBaseline(initial); setDraft(configOf(initial)); }
    else if (activeViewId && !remote && (!baseline || seenInSchema.current.has(activeViewId)) && !dirty && !conflict && initial) { setActive(initial.id); setBaseline(initial); setDraft(configOf(initial)); }
    else if (remote && !dirty && !conflict && remote.version > (baseline?.version ?? 0)) { setBaseline(remote); setDraft(configOf(remote)); }
  }, [schema.data, activeViewId, remote, dirty, conflict, baseline?.version]);
  useEffect(() => { if (activeViewId) sessionStorage.setItem(`workspace:${source}:view`, activeViewId); }, [source, activeViewId]);
  const select = (id: string) => {
    if (activeViewId) retained.current.set(activeViewId, { baseline, draft, conflict });
    const next = views.find(v => v.id === id); if (!next) return;
    const cached = retained.current.get(id);
    const previous = cached && (cached.conflict || cached.baseline && (cached.baseline.version > next.version || JSON.stringify(cached.draft) !== JSON.stringify(configOf(cached.baseline)))) ? cached : undefined;
    setActive(id); setBaseline(previous?.baseline ?? next); setDraft(previous?.draft ?? configOf(next)); setConflict(previous?.conflict ?? false); setError('');
  };
  const reload = async () => {
    try {
      const result = await schema.refetch();
      if (result.error) throw result.error;
      const next = result.data?.views.find(v => v.id === activeViewId);
      if (!next) { setError('视图已删除；草稿已保留，可另存副本。'); return; }
      // Explicit reload discards this view draft, after the user chooses the labelled control.
      setBaseline(next); setDraft(configOf(next)); setConflict(false); setError(''); retained.current.delete(activeViewId);
    } catch (e) { setError((e as Error).message); }
  };
  const save = useMutation({ mutationFn: async (copy: boolean) => {
    const result = await workspaceAction<ViewDefinition>({ action: 'view.save', input: { ...draft, ...(copy || baseline?.scope !== 'private' ? {} : { id: activeViewId, expectedVersion: baseline.version }) }, idempotencyKey: actionKey() });
    setReceipt(result.receipt); return result.data;
  }, onSuccess: (view) => { setActive(view.id); setBaseline(view); setDraft(configOf(view)); setError(''); setConflict(false); retained.current.delete(view.id); void qc.invalidateQueries({ queryKey: ['hub'] }); }, onError: (e) => { if ((e as ApiError).status === 409) setConflict(true); setError(`${e.message}。草稿已保留；可重读最新配置或另存副本。`); } });
  const remove = useMutation({ mutationFn: async () => {
    const result = await workspaceAction({ action: 'view.delete', input: { id: activeViewId, expectedVersion: baseline?.version }, idempotencyKey: actionKey() }); setReceipt(result.receipt);
  }, onSuccess: () => { retained.current.delete(activeViewId); setActive(''); setBaseline(null); setConflict(false); setError(''); void qc.invalidateQueries({ queryKey: workspaceKeys.schema }); }, onError: (e) => setError(e.message) });
  const patch = useCallback((next: Partial<ViewConfig>) => setDraft(d => ({ ...d, ...next })), []);
  return { schema, views, activeViewId, loadedVersion: baseline?.version, baseline, draft, patch, select, dirty, remoteChanged, conflict, error, reload, save, remove, receipt };
}
export type WorkspaceViewState = ReturnType<typeof useWorkspaceView>;
